import React, { memo, useCallback, useMemo, useRef, useState } from "react";

// dls
import { format } from "@swiggy-private/js-utils";
import ElementObserver from "@swiggy-private/dls-react-mxn/dist/ElementObserver";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import OTPInput from "@swiggy-private/dls-web-recipe-food-web/dist/components/OTPInputBox";

// constants
import {
    DATA_TEST_IDS,
    OBJECT_NAME,
    OBJECT_VALUES,
    PIN_LABEL,
    PIN_LENGTH,
    PIN_TEST_ID,
    TEXTS,
} from "./constant";

// styled
import { CTA, PinInputContainer, SubText, TitleText } from "./styled";

// types
import { IPinVerificationCard } from "./types";
import { useAnalytics } from "../hooks";

const PinVerificationCard = ({
    showHalfCard,
    pinErrorText,
    closeHalfCard,
    onCtaClick,
    userName = "HOST",
    screenName,
    impressionAnalytics: defaultImpressionAnalytics,
    clickAnalytics: defaultClickAnalytics,
    title,
    description,
    isLoading = false,
}: IPinVerificationCard) => {
    const { impressionAnalytics, clickAnalytics } = useAnalytics({
        screenName,
        defaultImpressionAnalytics,
        defaultClickAnalytics,
    });
    const [pinValue, setPinValue] = useState("");
    const otpInputRef = useRef<HTMLInputElement>(null);
    const hasImpressionFiredRef = useRef(false);

    const handlePinChange = (text: string) => {
        const numericText = text.replace(/\D/g, "");
        setPinValue(numericText);
    };

    const onHalfCardClose = useCallback(() => {
        if (isLoading) return;
        clickAnalytics?.({
            action: OBJECT_NAME,
            label: OBJECT_VALUES.CLICK_CLOSE_CTA,
            value: String(1),
            context: JSON.stringify({
                pin: pinValue,
            }),
        });
        closeHalfCard();
    }, [clickAnalytics, closeHalfCard, pinValue, isLoading]);

    const handleCtaClick = useCallback(() => {
        clickAnalytics?.({
            action: OBJECT_NAME,
            label: OBJECT_VALUES.CLICK_CONFIRM_CTA,
            value: String(1),
            context: JSON.stringify({
                pin: pinValue,
            }),
        });
        onCtaClick(pinValue);
    }, [clickAnalytics, onCtaClick, pinValue]);

    const onCardImpression = useCallback(() => {
        if (hasImpressionFiredRef.current) return;

        hasImpressionFiredRef.current = true;
        impressionAnalytics?.({
            action: OBJECT_NAME,
            label: OBJECT_VALUES.IMPRESSION_CONTAINER,
            value: String(1),
        });
    }, [impressionAnalytics]);

    const titleText = useMemo(() => {
        return title || format(TEXTS.TITLE, userName);
    }, [title, userName]);

    const subTitleText = useMemo(() => {
        return description || format(TEXTS.SUB_TITLE, userName);
    }, [description, userName]);

    const isCtaDisabled = useMemo(() => {
        return pinValue.length !== PIN_LENGTH || isLoading;
    }, [pinValue.length, isLoading]);

    return (
        <ElementObserver onViewEnter={onCardImpression} senseOnce>
            <HalfCard isOpen={showHalfCard} onClose={onHalfCardClose}>
                <TitleText
                    variant="V2.subheading1.neutral.bold"
                    color="text_Highest_Emphasis"
                    maxLines={1}
                >
                    {titleText}
                </TitleText>
                <SubText variant="V2.body2.neutral.medium" color="text_Med_Emphasis">
                    {subTitleText}
                </SubText>
                <div>
                    <PinInputContainer>
                        <OTPInput
                            value={pinValue}
                            onChange={handlePinChange}
                            length={PIN_LENGTH}
                            testId={PIN_TEST_ID}
                            ariaLabel={PIN_LABEL}
                            errorMsg={pinValue && pinErrorText}
                            ref={otpInputRef}
                        />
                    </PinInputContainer>
                </div>
                <CTA
                    data-testId={DATA_TEST_IDS.CTA}
                    onClick={handleCtaClick}
                    isDisabled={isCtaDisabled}
                    variant="primary"
                >
                    {TEXTS.CTA}
                </CTA>
            </HalfCard>
        </ElementObserver>
    );
};

export default memo(PinVerificationCard);
